import React, { useEffect, useRef, useState } from "react";
import FeaturePaywallModal from "../../../../../components/popups/paywall/FeaturePaywallModal";
import { canAccessPaywallFeature } from "../../../../../data/paywallFeatures";
import SignPdfIntentModal from "./SignPdfIntentModal";
import SignPdfSignatureModal from "./SignPdfSignatureModal";
import SignPdfWhoIsSigningModal from "./SignPdfWhoIsSigningModal";
import SignPdfSetupPanel from "./SignPdfSetupPanel";
import {
  createDefaultSignPdfInitialsSettings,
  createDefaultSignPdfSettings,
  hasSignPdfSignature,
  hasSignPdfSigningIntent,
  setSignPdfSigningIntent,
  setSignPdfWhoChosen,
  withDefaultSignPdfCanvas,
  SIGN_PDF_MARK_KINDS,
  SIGN_PDF_SCOPES,
} from "./signPdfSettings";
import {
  addSignPdfPlacement,
  addSignPdfSigningBlock,
  applySignPdfTemplateAndPlace,
  canPlaceSignPdfField,
  getSignPdfTemplate,
  SIGN_PDF_FIELD_KINDS,
} from "./signPdfPlacements";
import { SIGN_PDF_WORKFLOW_STEPS } from "./signPdfWorkflow";
import "../../../watermark/Popup/WatermarkControls.css";
import "./SignPdfControls.css";

/**
 * Sign PDF left panel + modals (guided workflow).
 */
const SignPdfControls = ({
  session,
  onSessionChange,
  disabled = false,
  currentPage = 1,
  autoOpen = false,
  hasDocument = false,
  pageCount = 0,
  fileSizeLabel = "",
  workflowStep = SIGN_PDF_WORKFLOW_STEPS.UPLOAD,
  reviewComplete = false,
  onActionsReady = null,
  onOpenFinish = () => {},
  onSignaturePlaced = () => {},
}) => {
  const [modalKind, setModalKind] = useState(null);
  const [intentOpen, setIntentOpen] = useState(false);
  const [whoOpen, setWhoOpen] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallFeatureKey, setPaywallFeatureKey] = useState(
    "sign_pdf_per_page"
  );
  const whoOpenedRef = useRef(false);
  const signaturePromptedRef = useRef(false);

  const signature = getSignPdfTemplate(session, SIGN_PDF_FIELD_KINDS.SIGNATURE);
  const initials = getSignPdfTemplate(session, SIGN_PDF_FIELD_KINDS.INITIALS);
  const hasSignature = hasSignPdfSignature(signature);
  const intentChosen = hasSignPdfSigningIntent(session);
  const whoChosen = Boolean(session?.signPdfWhoChosen);

  const openModal = (kind) => {
    if (disabled) return;
    setWhoOpen(false);
    setIntentOpen(false);
    setModalKind(
      kind === SIGN_PDF_FIELD_KINDS.INITIALS
        ? SIGN_PDF_MARK_KINDS.INITIALS
        : SIGN_PDF_MARK_KINDS.SIGNATURE
    );
  };

  const openFormatModal = () => {
    if (disabled) return;
    setIntentOpen(true);
  };

  // After upload — only the Sign myself modal.
  useEffect(() => {
    if (
      !autoOpen ||
      disabled ||
      !hasDocument ||
      whoChosen ||
      whoOpenedRef.current
    ) {
      return;
    }
    whoOpenedRef.current = true;
    setWhoOpen(true);
  }, [autoOpen, disabled, hasDocument, whoChosen]);

  // After format chosen — open create signature modal.
  useEffect(() => {
    if (
      !autoOpen ||
      disabled ||
      !whoChosen ||
      !intentChosen ||
      hasSignature ||
      signaturePromptedRef.current ||
      whoOpen ||
      intentOpen ||
      modalKind
    ) {
      return;
    }
    signaturePromptedRef.current = true;
    setModalKind(SIGN_PDF_MARK_KINDS.SIGNATURE);
  }, [
    autoOpen,
    disabled,
    whoChosen,
    intentChosen,
    hasSignature,
    whoOpen,
    intentOpen,
    modalKind,
  ]);

  const canPlaceSignature = canPlaceSignPdfField(
    session,
    SIGN_PDF_FIELD_KINDS.SIGNATURE
  );

  const placeDomainBlock = (block, sizeId, meta = {}) => {
    if (disabled || !intentChosen) return;
    if (!canPlaceSignature || meta.needSignature) {
      openModal(SIGN_PDF_FIELD_KINDS.SIGNATURE);
      return;
    }
    onSessionChange?.(
      addSignPdfSigningBlock(session, currentPage, {
        domainId: block?.id || "classic",
        presetId: sizeId || block?.defaultSizeId || "standard",
      })
    );
  };

  useEffect(() => {
    onActionsReady?.({
      openSignature: () => openModal(SIGN_PDF_FIELD_KINDS.SIGNATURE),
      openInitials: () => openModal(SIGN_PDF_FIELD_KINDS.INITIALS),
      openFormat: openFormatModal,
      placeField: (kind, patch = {}) => {
        if (disabled || !intentChosen) return;
        if (!canPlaceSignPdfField(session, kind)) {
          openModal(kind);
          return;
        }
        onSessionChange?.(
          addSignPdfPlacement(session, kind, currentPage, patch)
        );
      },
      canPlaceField: (kind) =>
        intentChosen && canPlaceSignPdfField(session, kind),
      placeBlock: placeDomainBlock,
      canPlaceBlock: () => canPlaceSignature,
      triggerBlockPaywall: () => {},
    });
  }, [
    session,
    disabled,
    intentChosen,
    currentPage,
    onSessionChange,
    onActionsReady,
    canPlaceSignature,
  ]);

  const handleSignMyself = () => {
    setWhoOpen(false);
    onSessionChange?.(setSignPdfWhoChosen(session, true));
    setIntentOpen(true);
  };

  const chooseIntent = (scope) => {
    if (disabled) return;
    if (
      scope === SIGN_PDF_SCOPES.PER_PAGE &&
      !canAccessPaywallFeature("sign_pdf_per_page")
    ) {
      setPaywallFeatureKey("sign_pdf_per_page");
      setPaywallOpen(true);
      return;
    }
    onSessionChange?.(setSignPdfSigningIntent(session, scope, currentPage));
    setIntentOpen(false);
  };

  const applyMark = (draft) => {
    if (!modalKind) return;
    const isSignature =
      modalKind === SIGN_PDF_MARK_KINDS.SIGNATURE ||
      modalKind === SIGN_PDF_FIELD_KINDS.SIGNATURE;
    const placePage = isSignature ? 1 : currentPage;
    onSessionChange?.(
      applySignPdfTemplateAndPlace(session, modalKind, draft, placePage)
    );
    setModalKind(null);
    if (isSignature) {
      onSignaturePlaced?.();
    }
  };

  const modalSeed =
    modalKind === SIGN_PDF_MARK_KINDS.INITIALS
      ? hasSignPdfSignature(initials)
        ? initials
        : withDefaultSignPdfCanvas(createDefaultSignPdfInitialsSettings())
      : hasSignPdfSignature(signature)
        ? signature
        : withDefaultSignPdfCanvas(createDefaultSignPdfSettings());

  return (
    <>
      <div
        className={`wmc sp-controls-root sp-controls-root--guided${
          disabled ? " is-disabled" : ""
        }`}
      >
        <SignPdfSetupPanel
          session={session}
          workflowStep={workflowStep}
          reviewComplete={reviewComplete}
          currentPage={currentPage}
          hasDocument={hasDocument}
          pageCount={pageCount}
          fileSizeLabel={fileSizeLabel}
          disabled={disabled}
          onEditSignature={() => openModal(SIGN_PDF_FIELD_KINDS.SIGNATURE)}
          onChooseFormat={openFormatModal}
          onOpenFinish={onOpenFinish}
        />
      </div>

      <SignPdfIntentModal
        open={intentOpen}
        onClose={() => setIntentOpen(false)}
        onSelect={chooseIntent}
      />

      <SignPdfWhoIsSigningModal
        open={whoOpen}
        onClose={() => setWhoOpen(false)}
        onSignMyself={handleSignMyself}
      />

      <SignPdfSignatureModal
        key={modalKind || "closed"}
        open={Boolean(modalKind)}
        kind={modalKind || SIGN_PDF_MARK_KINDS.SIGNATURE}
        initialSettings={modalSeed}
        onClose={() => setModalKind(null)}
        onApply={applyMark}
      />

      <FeaturePaywallModal
        open={paywallOpen}
        featureKey={paywallFeatureKey}
        toolKey="sign_pdf"
        onClose={() => setPaywallOpen(false)}
      />
    </>
  );
};

export default SignPdfControls;
